import { useCallback, useEffect, useReducer, useState, type FormEvent, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import SegmentedControl from '../../../components/base/SegmentedControl';
import Combobox from '../../../components/base/Combobox';
import CityCombobox from '../../../components/search/CityCombobox';
import PropertyTypePicker from '../../../components/search/PropertyTypePicker';
import RangeFilterGrid from '../../../components/search/RangeFilterGrid';
import { useLocationSuggestions } from '../../../hooks/useLocationSuggestions';
import { useSaudiCities, type ResolvedSaudiCity } from '../../../hooks/useSaudiCities';
import type { LocationSuggestion } from '../../../services/neighborhoodMapService';
import { buildHeroSearchParams } from '../../../utils/heroSearchParams';
import {
  activeRangeCount,
  createHeroFormState,
  firstInvertedRange,
  heroFormReducer,
  suggestionLabel,
  toHeroSearchInput,
  type HeroCategory,
  type HeroListing,
} from './heroSearchState';

/**
 * The home hero's search console. See Apps/web/THEME.md §8 ("Search bar").
 *
 * A deep-teal panel inside the hero photo, in the manner of rightmove.co.uk's
 * (owner, 2026-10-08): underline tabs, a short label, one white bar, one button.
 *
 *    للإيجار   للبيع                                   [سكني | تجاري]
 *   ━━━━━━━──────────────────────────────────────────────────────────
 *   ابحث عن عقار للإيجار
 *   ┌──────────┬────────────────────────┬────────────┐ ┌────────┐
 *   │ المدينة  │ الحي أو صف ما تريد      │ نوع العقار │ │ 🔍 بحث │  red = search
 *   └──────────┴────────────────────────┴────────────┘ └────────┘
 *   الفلاتر (n) ⌄                                  ابحث على الخريطة
 *   [ غرف النوم | دورات المياه | السعر [شهري|سنوي] | المساحة ]  «مسح الكل»  «بحث»
 *
 * - DOM order is the reading order, so in Arabic the bar reads right to left:
 *   city, text, type, button. Below `md` it stacks (city, text, then type beside the
 *   button); every cell is there on a phone.
 * - Every list opens BELOW its field (Popover), never upward.
 * - Type lives in the bar only and price in «الفلاتر» only: nothing is asked twice.
 * - The text field takes a district (exact `areaId`) or plain words (`q`, read by
 *   the search's query parser). A city suggestion moves into the city picker.
 * - The URL always carries `listingType` and `category`; a city goes as `cityId` +
 *   name. A value hidden for the chosen type or category is never sent.
 * - A «من» above its «إلى» stops the search and takes the user to that field.
 * State rules: ./heroSearchState.ts. URL contract: utils/heroSearchParams.ts.
 */

const CITY_ID = 'hero-city';
const QUERY_ID = 'hero-query';
const TYPE_ID = 'hero-type';
const PANEL_ID = 'hero-filters';
/** RangeFilterGrid field ids: `${RANGE_ID_PREFIX}-<key>-min|max`. */
const RANGE_ID_PREFIX = 'hero-f';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:focus-visible:ring-[#3FBBA2]/25';
/** The same ring for a control that sits on the deep-teal panel. */
const FOCUS_RING_ON_DARK = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70';

function SuggestionRow({
  location,
  isArabic,
  cityWord,
  districtWord,
}: {
  location: LocationSuggestion;
  isArabic: boolean;
  cityWord: string;
  districtWord: string;
}) {
  const isCity = location.kind === 'city';
  const parent = isArabic
    ? location.cityNameAr || location.cityNameEn
    : location.cityNameEn || location.cityNameAr;
  const detail = isCity ? cityWord : parent ? `${districtWord} · ${parent}` : districtWord;
  return (
    <span className="flex min-w-0 items-center gap-3">
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]"
        aria-hidden="true"
      >
        <i className={`${isCity ? 'ri-building-2-line' : 'ri-map-pin-2-line'} text-[1.375rem] leading-none`} />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[0.9375rem] text-[var(--pv-color-text)]">
          {suggestionLabel(location, isArabic)}
        </span>
        <span className="block truncate text-[0.875rem] text-[var(--pv-color-text-muted)]">{detail}</span>
      </span>
    </span>
  );
}

export default function HeroSearch() {
  const { t, i18n } = useTranslation('hero');
  // `t` with {{vars}}, always a string (a test double may hand back the options object).
  const textWith = (key: string, vars: Record<string, string | number>): string => {
    const out: unknown = t(key, vars);
    return typeof out === 'string' ? out : key;
  };
  const language = typeof i18n?.language === 'string' ? i18n.language : 'ar';
  const isArabic = language.startsWith('ar');
  const isRTL = typeof i18n?.dir === 'function' ? i18n.dir() === 'rtl' : isArabic;
  const navigate = useNavigate();
  const reduce = useReducedMotion() ?? false;
  const { findByCityId, findByName } = useSaudiCities();

  const [state, dispatch] = useReducer(heroFormReducer, undefined, createHeroFormState);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  // A field to focus once it is on screen (the first «من / إلى» the user must fix).
  const [focusId, setFocusId] = useState<string | null>(null);

  // Suggestions for the text field: none while it shows a picked district. Scoped
  // to the chosen city's districts once that city's database id is known.
  const { locations, loadingLocations } = useLocationSuggestions(
    state.location ? '' : state.text,
    state.city?.cityId ?? undefined,
  );

  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(focusId);
    if (!el) return;
    el.focus();
    setFocusId(null);
  }, [focusId, filtersOpen]);

  const activeCount = activeRangeCount(state);

  const pickCity = useCallback((city: ResolvedSaudiCity | null) => dispatch({ type: 'city', city }), []);

  /** The canonical city a database row belongs to: by id, then by any of its names. */
  const canonicalCity = (id: string | null | undefined, ...names: Array<string | null | undefined>) => {
    const byId = findByCityId(id);
    if (byId) return byId;
    for (const name of names) {
      const hit = findByName(name);
      if (hit) return hit;
    }
    return undefined;
  };

  const pickSuggestion = (location: LocationSuggestion) => {
    const isCity = location.kind === 'city';
    const canonical = isCity
      ? canonicalCity(location.id, location.nameAr, location.nameEn)
      : canonicalCity(location.cityId, location.cityNameAr, location.cityNameEn);
    dispatch({
      type: 'suggestion',
      location,
      label: suggestionLabel(location, isArabic),
      // The suggestion's own row id: the exact city the user picked.
      city: canonical ? { ...canonical, cityId: isCity ? location.id : location.cityId } : null,
    });
  };

  /** Opens «الفلاتر» on the first «من» above its «إلى». True when the search must wait. */
  const revealInvalidRange = (): boolean => {
    const key = firstInvertedRange(state);
    if (!key) return false;
    setFiltersOpen(true);
    setFocusId(`${RANGE_ID_PREFIX}-${key}-min`);
    return true;
  };

  const searchHref = (view?: 'map') =>
    `/search?${buildHeroSearchParams(toHeroSearchInput(state, { isRTL, view })).toString()}`;

  const submit = () => {
    if (revealInvalidRange()) return;
    navigate(searchHref());
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const onMapClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (revealInvalidRange()) event.preventDefault();
  };

  // «اختر نوع العقار…» in the panel opens the type picker in the bar.
  const requestType = () => {
    document.getElementById(TYPE_ID)?.focus();
    setTypeOpen(true);
  };

  const linkButton = `inline-flex min-h-[2.75rem] items-center gap-2 rounded-lg px-2 text-[0.9375rem] leading-tight transition-colors ${FOCUS_RING}`;
  const linkButtonOnDark = `inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2 text-[0.9375rem] leading-tight transition-colors ${FOCUS_RING_ON_DARK}`;

  return (
    <form
      role="search"
      aria-label={t('bar.label')}
      noValidate
      onSubmit={onSubmit}
      className="w-full rounded-[24px] bg-[#003930] p-4 text-white shadow-[0_24px_60px_-28px_rgba(0,0,0,0.65)] ring-1 ring-white/10 sm:p-6 short:p-4"
    >
      {/* The tabs over a hairline that runs across the panel, with the category
          right next to them (owner, 2026-10-08: "keep it like before"). On a phone
          the two sit at either end of the row so they never wrap. */}
      <div className="flex flex-nowrap items-end justify-between gap-x-2 border-b border-white/15 sm:justify-start sm:gap-x-8">
        <SegmentedControl<HeroListing>
          variant="underline"
          tone="onDark"
          size="sm"
          ariaLabel={t('tabs.label')}
          value={state.listing}
          onChange={(listing) => dispatch({ type: 'listing', listing })}
          options={[
            { value: 'rent', label: t('tabs.rent') },
            { value: 'buy', label: t('tabs.buy') },
          ]}
          className="border-b-transparent"
        />
        <SegmentedControl<HeroCategory>
          size="sm"
          tone="onDark"
          ariaLabel={t('category.label')}
          value={state.category}
          onChange={(category) => dispatch({ type: 'category', category })}
          options={[
            { value: 'residential', label: t('category.residential') },
            { value: 'commercial', label: t('category.commercial') },
          ]}
          className="mb-1.5 shrink-0"
        />
      </div>

      {/* The label for the bar; dropped on a very short window. */}
      <p className="mb-0 mt-4 text-[0.9375rem] font-semibold text-white short:mt-3 tiny:hidden">
        {state.listing === 'buy' ? t('panel.buyLabel') : t('panel.rentLabel')}
      </p>

      {/* THE BAR: one white rounded container, hairline dividers, the red square at
          the end. The square lines up with the last row when the bar stacks. */}
      <div className="mt-2 flex items-end gap-2 sm:gap-3">
        <div className="grid min-w-0 flex-1 grid-cols-1 overflow-hidden rounded-xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] text-[var(--pv-color-text)] md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_minmax(0,12rem)] lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)_minmax(0,15rem)] xl:grid-cols-[minmax(0,18rem)_minmax(0,1fr)_minmax(0,18rem)]">
          <div className="h-[50px] min-w-0 border-b border-[var(--pv-border-soft)] md:border-b-0 md:border-e">
            <CityCombobox
              id={CITY_ID}
              variant="bar"
              inlineLabel
              value={state.city}
              onChange={pickCity}
              label={t('bar.city')}
              placeholder={t('bar.allCities')}
              className="h-full"
            />
          </div>
          <div className="h-[50px] min-w-0 border-b border-[var(--pv-border-soft)] md:border-b-0 md:border-e">
            <Combobox<LocationSuggestion>
              id={QUERY_ID}
              variant="bare"
              inlineLabel
              label={t('bar.query')}
              placeholder={t('bar.queryPlaceholder')}
              leadingIcon={<i className="ri-search-line" />}
              inputValue={state.text}
              onInputChange={(text) => dispatch({ type: 'text', text })}
              options={state.location ? [] : locations}
              getOptionKey={(location) => `${location.kind}:${location.id}`}
              renderOption={(location) => (
                <SuggestionRow
                  location={location}
                  isArabic={isArabic}
                  cityWord={t('suggestions.city')}
                  districtWord={t('suggestions.district')}
                />
              )}
              onSelect={pickSuggestion}
              selectedKey={state.location ? `${state.location.kind}:${state.location.id}` : null}
              loading={!state.location && loadingLocations}
              // Plain words are a description, not a failed lookup: no "no results" row.
              emptyText={null}
              allowFreeText
              onSubmitFreeText={submit}
              listLabel={t('suggestions.label')}
              popoverMinWidth="18rem"
              className="h-full"
            />
          </div>
          <div className="h-[50px] min-w-0">
            <PropertyTypePicker
              id={TYPE_ID}
              variant="popover"
              triggerVariant="bare"
              inlineLabel
              label={t('bar.type')}
              category={state.category}
              value={state.propertyType}
              onChange={(propertyType) => dispatch({ type: 'propertyType', propertyType })}
              open={typeOpen}
              onOpenChange={setTypeOpen}
              className="h-full"
            />
          </div>
        </div>

        {/* The red button: the board's search CTA (THEME.md §2.4c, §8 rule 3). A
            square on a phone; from sm it says «بحث», as rightmove's does. */}
        <button
          type="submit"
          aria-label={t('bar.search')}
          title={t('bar.search')}
          className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-xl bg-[#DF2F2B] text-white transition-colors duration-200 hover:bg-[#B8171C] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:flex sm:w-auto sm:items-center sm:gap-2 sm:px-7"
        >
          <i className="ri-search-line text-[1.375rem] leading-none sm:text-[1.25rem]" aria-hidden="true" />
          <span className="hidden text-[1rem] font-semibold sm:inline" aria-hidden="true">
            {t('bar.search')}
          </span>
        </button>
      </div>

      {/* «الفلاتر» toggle + map link: one row even on a phone. */}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2">
        <button
          type="button"
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={filtersOpen}
          aria-controls={filtersOpen ? PANEL_ID : undefined}
          // Spelled out for screen readers; the badge alone would read as a bare number.
          aria-label={activeCount > 0 ? textWith('filters.toggleActive', { n: activeCount }) : undefined}
          className={`${linkButtonOnDark} text-white/90 hover:bg-white/10 hover:text-white`}
        >
          <i className="ri-equalizer-line text-[1.375rem] leading-none" aria-hidden="true" />
          <span>{t('filters.toggle')}</span>
          {activeCount > 0 && (
            <span
              className="grid h-5 min-w-5 place-items-center rounded-full bg-white px-1.5 text-xs font-semibold leading-none text-[#003930]"
              aria-hidden="true"
            >
              {activeCount}
            </span>
          )}
          <i
            className={`ri-arrow-down-s-line text-[1.125rem] leading-none transition-transform duration-200 ${
              filtersOpen ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>

        <Link
          to={searchHref('map')}
          onClick={onMapClick}
          className={`${linkButtonOnDark} text-white/90 hover:bg-white/10 hover:text-white`}
        >
          <i className="ri-map-pin-2-line text-[1.375rem] leading-none" aria-hidden="true" />
          <span>{t('map')}</span>
        </Link>
      </div>

      {/* «الفلاتر»: the «من / إلى» grid for the fields the type allows. No type and
          no city here: both live in the bar. */}
      <AnimatePresence initial={false}>
        {filtersOpen && (
          <motion.div
            key="filters"
            id={PANEL_ID}
            role="group"
            aria-label={t('filters.panel')}
            initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-2 rounded-xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-soft)] p-3 text-[var(--pv-color-text)] sm:p-4">
              <RangeFilterGrid
                idPrefix={RANGE_ID_PREFIX}
                propertyType={state.propertyType}
                category={state.category}
                listingType={state.listing}
                value={state.ranges}
                onChange={(key, bounds) => dispatch({ type: 'range', key, bounds })}
                pricePeriod={state.pricePeriod}
                onPricePeriodChange={(period) => dispatch({ type: 'pricePeriod', period })}
                onRequestType={requestType}
              />
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-[var(--pv-border-soft)] pt-3">
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'clearRanges' })}
                  disabled={activeCount === 0}
                  className={`${linkButton} text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-primary)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-[var(--pv-color-text-muted)]`}
                >
                  {t('filters.clearAll')}
                </button>
                <button
                  type="submit"
                  className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-xl bg-[#004D40] px-5 text-[0.9375rem] leading-tight text-white transition-colors duration-200 hover:bg-[#003930] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--pv-color-primary)] dark:bg-[#3FBBA2] dark:text-[#041C18] dark:hover:bg-[#7FD6C3]"
                >
                  <i className="ri-search-line text-[1.125rem] leading-none" aria-hidden="true" />
                  <span>{t('filters.submit')}</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  );
}
